import { createTheme } from '@mui/material/styles';

// Keeps the look of the original Mantis template (Ant Design palette, Public
// Sans), trimmed to what the CRM uses. Theme code derived from Mantis (MIT).
const grey = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#f0f0f0',
  300: '#d9d9d9',
  400: '#bfbfbf',
  500: '#8c8c8c',
  600: '#595959',
  700: '#262626',
  800: '#141414',
  900: '#000000',
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { light: '#69b1ff', main: '#1677ff', dark: '#0958d9', contrastText: '#fff' },
    secondary: { light: grey[300], main: grey[500], dark: grey[700], contrastText: '#fff' },
    error: { light: '#ffa39e', main: '#ff4d4f', dark: '#a8071a', contrastText: '#fff' },
    warning: { light: '#ffd666', main: '#faad14', dark: '#ad6800', contrastText: grey[100] },
    info: { light: '#5cdbd3', main: '#13c2c2', dark: '#006d75', contrastText: '#fff' },
    success: { light: '#95de64', main: '#52c41a', dark: '#237804', contrastText: '#fff' },
    grey,
    text: { primary: grey[700], secondary: grey[500], disabled: grey[400] },
    divider: grey[200],
    background: { default: '#fafafb', paper: '#ffffff' },
  },
  shape: { borderRadius: 6 },
  typography: {
    fontFamily: `'Public Sans', sans-serif`,
    h1: { fontWeight: 600, fontSize: '2.375rem', lineHeight: 1.21 },
    h2: { fontWeight: 600, fontSize: '1.875rem', lineHeight: 1.27 },
    h3: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.33 },
    h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4 },
    h5: { fontWeight: 600, fontSize: '1rem', lineHeight: 1.5 },
    h6: { fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.57 },
    body1: { fontSize: '0.875rem', lineHeight: 1.57 },
    body2: { fontSize: '0.75rem', lineHeight: 1.66 },
    subtitle1: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.57 },
    subtitle2: { fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.66 },
    caption: { fontSize: '0.75rem', lineHeight: 1.66 },
    button: { textTransform: 'none', fontWeight: 500 },
  },
  components: {
    MuiButton: { defaultProps: { disableElevation: true } },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: { root: { borderColor: grey[200] } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: grey[200], padding: '12px 16px' },
        head: { fontWeight: 600, color: grey[600], backgroundColor: grey[50] },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 500 } } },
    MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiDialogTitle: { styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 600 } } },
    MuiDialog: {
      styleOverrides: {
        // Our dialogs wrap content + actions in a <form>. Let it flex so only DialogContent
        // scrolls and the title and buttons stay in view.
        paper: { '& > form': { display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 } },
      },
    },
    // A neutral track, so an empty bar doesn't read as a full one.
    MuiLinearProgress: { styleOverrides: { root: { backgroundColor: grey[200] } } },
  },
});
